// vavci-pdp-tabs.js — Pestañas de la ficha (Descripción / Cómo usarlo / Ingredientes /
// Envío y devoluciones) del rediseño Concept (WU2).
//
// La descripción se trocea EN EL CLIENTE, nunca en metafields: TikTok Shop y otros
// canales leen descriptionHtml completo, así que la ficha almacenada sigue entera y
// aquí solo se REPARTE lo ya renderizado. Puntos de corte: h3 dentro de la
// descripción, por id si lo hay y si no por el TEXTO del título (sin distinguir
// mayúsculas ni acentos):
//   «modo de uso | cómo usarlo | uso»       → pestaña Cómo usarlo
//   «ingredientes | composición | activos»  → pestaña Ingredientes
// Lo que no encaja se queda en Descripción. La pestaña de Envío no sale de la
// descripción: es contenido de tienda (ajuste richtext de la sección).
//
// Mejora progresiva: el Liquid pinta la descripción completa visible; este script
// reparte segmentos, y solo si hay 2+ pestañas con contenido activa la botonera.
// Sin JS (o sin nada que repartir y sin texto de envío) la ficha queda como antes:
// un único bloque «Descripción».
//
// Las funciones de troceo son PURAS (ni DOM ni estado): se prueban desde
// scripts/__tests__/pdp-tabs.test.js con node:test.

// ── Troceo puro ──────────────────────────────────────────────────────────────

const ENTITIES = {
  amp: '&', nbsp: ' ', aacute: 'á', eacute: 'é', iacute: 'í', oacute: 'ó', uacute: 'ú',
  Aacute: 'Á', Eacute: 'É', Iacute: 'Í', Oacute: 'Ó', Uacute: 'Ú', ntilde: 'ñ', Ntilde: 'Ñ',
  uuml: 'ü', Uuml: 'Ü', iquest: '¿', quot: '"',
};

/** Texto plano de un fragmento HTML: fuera etiquetas, entidades comunes decodificadas. */
function plainText(html) {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
    .replace(/&([a-zA-Z]+);/g, (_, name) => ENTITIES[name] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Normaliza para comparar: minúsculas, sin acentos, guiones/underscores como espacio. */
function normalize(text) {
  return String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Trocea el HTML de la descripción en segmentos delimitados por h3 (tokenizador
 * mínimo por regex: en el RTE de Shopify los h3 son de primer nivel).
 * Cada segmento conserva SU h3 dentro (el rótulo del comerciante no se pierde).
 * @param {string} html
 * @returns {Array<{id: string|null, title: string|null, html: string}>}
 */
export function splitByH3(html) {
  if (!html) return [];
  const re = /<h3\b([^>]*)>([\s\S]*?)<\/h3\s*>/gi;
  const segments = [];
  let cursor = 0;
  let current = null; // segmento abierto (null = preámbulo)
  let m;
  while ((m = re.exec(html)) !== null) {
    const before = html.slice(cursor, m.index);
    if (current) current.html += before;
    else if (before.trim()) segments.push({ id: null, title: null, html: before });
    const idMatch = /\bid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(m[1]);
    const id = idMatch ? (idMatch[1] ?? idMatch[2] ?? idMatch[3]) : null;
    const title = plainText(m[2]) || null;
    current = { id, title, html: m[0] };
    segments.push(current);
    cursor = re.lastIndex;
  }
  const tail = html.slice(cursor);
  if (current) current.html += tail;
  else if (tail.trim()) segments.push({ id: null, title: null, html: tail });
  return segments;
}

const RULES = [
  { tab: 'uso', re: /\bmodo de uso\b|\bcomo usarlo\b|\busos?\b/ },
  { tab: 'ingredientes', re: /\bingredientes\b|\bcomposicion\b|\bactivos\b/ },
];

/**
 * Pestaña destino de un encabezado: primero por id, y si el id no encaja, por el
 * texto del título. Insensible a mayúsculas, acentos y guiones.
 * @param {{id: string|null, title: string|null}} heading
 * @returns {'uso'|'ingredientes'|null}
 */
export function tabForHeading({ id, title }) {
  for (const candidate of [id, title]) {
    if (!candidate) continue;
    const norm = normalize(candidate);
    for (const { tab, re } of RULES) if (re.test(norm)) return tab;
  }
  return null;
}

/**
 * Reparto completo: HTML de la descripción → HTML por pestaña. Los segmentos no
 * clasificados (preámbulo y h3 ajenos) se quedan en descripción, en su orden.
 * @param {string} html
 * @returns {{descripcion: string, uso: string, ingredientes: string}}
 */
export function splitDescription(html) {
  const out = { descripcion: '', uso: '', ingredientes: '' };
  for (const seg of splitByH3(html)) {
    out[tabForHeading(seg) ?? 'descripcion'] += seg.html;
  }
  return out;
}

/**
 * Paneles con contenido, en el orden fijo de la ficha. Es la MISMA disponibilidad
 * para los dos modos (pestañas en escritorio, acordeón en móvil ≤900px): un panel
 * vacío no gana ni botón ni fila de acordeón.
 * @param {{descripcion?: string, uso?: string, ingredientes?: string}} parts
 * @param {boolean} hasShipping — la pestaña de Envío no sale de la descripción
 *   (es contenido de tienda); se decide fuera, por el data-vc-has-content del panel.
 * @returns {string[]} claves entre descripcion|uso|ingredientes|envio, en orden.
 */
export function availablePanels(parts, hasShipping) {
  const keys = [];
  for (const key of ['descripcion', 'uso', 'ingredientes']) {
    if (parts && typeof parts[key] === 'string' && parts[key].trim()) keys.push(key);
  }
  if (hasShipping) keys.push('envio');
  return keys;
}

// ── Adaptador DOM (mejora progresiva) ────────────────────────────────────────

function initTabs(root) {
  const nav = root.querySelector('[data-vc-tabs-nav]');
  const panels = new Map();
  root.querySelectorAll('[data-vc-tab-panel]').forEach((p) => panels.set(p.getAttribute('data-vc-tab-panel'), p));
  const descPanel = panels.get('descripcion');
  if (!nav || !descPanel) return;

  const descBody = descPanel.querySelector('.vc-rte');
  const parts = splitDescription(descBody ? descBody.innerHTML : '');

  // Una pestaña sin contenido se esconde a sí misma (ni botón ni panel vacío).
  // La disponibilidad se decide ANTES de tocar el DOM con availablePanels (la
  // misma lista vale para pestañas y acordeón): si al final no hay 2+ paneles
  // útiles, no se reparte nada y queda la maqueta de Liquid intacta
  // (encabezado «Descripción» + texto completo, sin filas de acordeón).
  const envioPanel = panels.get('envio');
  const avail = availablePanels(parts, Boolean(envioPanel && envioPanel.hasAttribute('data-vc-has-content')));
  const allButtons = [...nav.querySelectorAll('[data-vc-tab]')];
  const tabs = allButtons.filter((btn) => avail.includes(btn.getAttribute('data-vc-tab')));
  if (tabs.length < 2) return;

  for (const btn of allButtons) {
    const key = btn.getAttribute('data-vc-tab');
    if (!avail.includes(key)) {
      btn.hidden = true;
      const p = panels.get(key);
      if (p) p.hidden = true;
    }
  }
  for (const key of ['uso', 'ingredientes']) {
    const panel = panels.get(key);
    const body = panel && panel.querySelector('.vc-rte');
    if (body && parts[key]) body.innerHTML = parts[key];
  }
  if (descBody && (parts.uso || parts.ingredientes)) descBody.innerHTML = parts.descripcion;

  const select = (btn, focus) => {
    for (const b of tabs) {
      const on = b === btn;
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
      b.classList.toggle('is-active', on);
      const p = panels.get(b.getAttribute('data-vc-tab'));
      if (p) p.hidden = !on;
    }
    if (focus) btn.focus();
  };

  nav.addEventListener('click', (ev) => {
    const btn = ev.target.closest('[data-vc-tab]');
    if (btn && !btn.hidden) select(btn);
  });
  // Patrón WAI-ARIA tabs: flechas para moverse entre pestañas visibles.
  nav.addEventListener('keydown', (ev) => {
    if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') return;
    const cur = tabs.findIndex((b) => b.getAttribute('aria-selected') === 'true');
    if (cur === -1) return;
    ev.preventDefault();
    const next = (cur + (ev.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
    select(tabs[next], true);
  });

  const fallbackHead = root.querySelector('[data-vc-tabs-fallback]');
  if (fallbackHead) fallbackHead.hidden = true;
  nav.hidden = false;
  select(tabs[0]);

  // ── Acordeón móvil (≤900px) ──
  // EL CSS LLEVA EL MODO: en escritorio las cabeceras están display:none y manda
  // el estado de pestañas (hidden en los paneles); en móvil la botonera se oculta
  // y los paneles disponibles ([data-vc-acc-on]) se pintan como filas de acordeón
  // (rejilla 0fr→1fr) por encima de ese hidden. Cada modo lee SOLO su parte del
  // estado, así que cruzar los 900px en un resize no deja estados mixtos y no
  // hace falta escuchar matchMedia.
  const heads = new Map();
  root.querySelectorAll('[data-vc-acc-head]').forEach((h) => heads.set(h.getAttribute('data-vc-acc-head'), h));
  avail.forEach((key, i) => {
    const head = heads.get(key);
    const panel = panels.get(key);
    if (!head || !panel) return;
    head.hidden = false;
    panel.setAttribute('data-vc-acc-on', '');
    // La primera fila disponible («Descripción») abierta de serie, como la FAQ.
    if (i === 0) panel.classList.add('is-open');
    head.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
    // La última fila pinta la línea de cierre (las demás separan con border-top).
    if (i === avail.length - 1) panel.classList.add('vc-acc--last');
  });
  root.addEventListener('click', (ev) => {
    const head = ev.target.closest('[data-vc-acc-head]');
    if (!head || head.hidden) return;
    const willOpen = head.getAttribute('aria-expanded') !== 'true';
    // Una fila abierta a la vez (mismo comportamiento que la FAQ).
    heads.forEach((h) => h.setAttribute('aria-expanded', 'false'));
    root.querySelectorAll('[data-vc-tab-panel].is-open').forEach((p) => p.classList.remove('is-open'));
    if (willOpen) {
      head.setAttribute('aria-expanded', 'true');
      const panel = panels.get(head.getAttribute('data-vc-acc-head'));
      if (panel) panel.classList.add('is-open');
    }
  });
}

function init() {
  document.querySelectorAll('[data-vc-tabs]').forEach(initTabs);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
